/* eslint-disable react-hooks/exhaustive-deps */
/** Vendor imports */
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Modal } from 'antd';

import API from 'src/apis';
import { useAppSelector } from './redux/store';
import { setIsPollingActive, setPollingTimeLeft, setUserData } from './redux/slices/timer';

/** Page imports */
import Homepage from 'src/pages/homepage';
import HistoryPage from 'src/pages/history';
import LoginPage from 'src/pages/loginpage';
import ProfilePage from './pages/profilepage';
import PDFChat from './pages/pdf-chat';
import SubscriptionPage from './pages/subscription';
import WelcomePage from './pages/welcomepage';
import LogoutPage from './pages/logoutpage';

/** Components imports */
import SidebarBox from 'src/components/organisms/sidebar';

/** Style imports */
import styles from './App.module.scss';

const AppContent = () => {
  const dispatch = useDispatch();

  const { isPollingActive, pollingTimeLeft, userData, initialLiveAPICallTime } = useAppSelector(state => state.timer);

  const [width, setWidth] = useState<number>(window.innerWidth);
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [callLiveAPI, setCallLiveAPI] = useState<boolean>(!!localStorage.getItem('CALL_LIVE_API'));
  const [isWelcomeScreen, setIsWelcomeScreen] = useState<boolean>(true);

  window.addEventListener('load', async () => {
    try {
      if (isLogin && localStorage.getItem('token')) {
        const { data: userProfile = null } = await API.GetProfile();
        if (userProfile) {
          localStorage.setItem('userDetails', JSON.stringify(userProfile));
        }
      }
    } catch (error) {
      console.log(error);
    }
  });

  const getUserDetails = async () => {
    try {
      const { data: userProfile = null } = await API.GetProfile();
      if (userProfile) {
        if (userProfile?.subscribed_plan?.length !== userData?.subscribed_plan?.length) {
          localStorage.removeItem('POLLING_TIME_LEFT');
          dispatch(setIsPollingActive(false));
          localStorage.setItem('CALL_LIVE_API', initialLiveAPICallTime.toString());
          setCallLiveAPI(true);
        }
        localStorage.setItem('userDetails', JSON.stringify(userProfile));
        dispatch(setUserData(userProfile));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    const synth = window.speechSynthesis;
    synth?.cancel();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuthTokenInUrl = window.location.href.includes('authToken');

    if (isAuthTokenInUrl) {
      setIsLogin(['/history', '/'].includes(window.location.pathname));
    } else {
      setIsLogin(!!token);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!callLiveAPI) return;
    const interval = setInterval(() => {
      const newTime = parseInt(localStorage.getItem('CALL_LIVE_API') ?? '1') - 1;
      localStorage.setItem('CALL_LIVE_API', newTime.toString());
      if (newTime === 0) {
        localStorage.removeItem('CALL_LIVE_API');
        setCallLiveAPI(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [callLiveAPI]);

  useEffect(() => {
    if (!isPollingActive || pollingTimeLeft <= 0) {
      if (pollingTimeLeft === 0 && isPollingActive) {
        dispatch(setIsPollingActive(false));
        localStorage.removeItem('POLLING_TIME_LEFT');

        Modal.confirm({
          type: 'error',
          title: 'Payment Failed!',
          content: 'It seems there was an issue processing your payment. Rest assured, any amount deducted from your account will be refunded within 7 business days. If you have any questions or need further assistance, please feel free to contact our customer support team. We recommend trying the payment again after some time. Thank you for your understanding!',
          centered: true,
          onOk() { Modal.destroyAll(); },
          okButtonProps: { style: { background: 'linear-gradient(135deg, #4B22E5, #A553EE)' } },
          style: {
            backgroundImage: 'linear-gradient(135deg, #4B22E5, #A553EE)',
            borderColor: 'transparent'
          },
          cancelButtonProps: { style: { display: 'none' } }
        });
      }
      return;
    }

    const interval = setInterval(() => {
      const newTime = pollingTimeLeft - 1;
      localStorage.setItem('POLLING_TIME_LEFT', newTime.toString());
      dispatch(setPollingTimeLeft(newTime));
    }, 1000);

    // Call API in 3 sec...
    if (pollingTimeLeft % 3 === 0) {
      getUserDetails();
    }

    return () => clearInterval(interval);
  }, [isPollingActive, pollingTimeLeft]);

  const location = useLocation();
  useEffect(() => {
    setIsWelcomeScreen(['/welcome', '/logout'].includes(location.pathname));
  }, [location.pathname]);

  return (
    <div className={styles.App}>
      {(width > 900 && !isWelcomeScreen) && <SidebarBox />}
      {!loading && (
        <>
          {!isLogin ? (
            <Routes>
              <Route path='/login' element={<LoginPage />} />
              <Route path='/logout' element={<LogoutPage />} />
              <Route path='*' element={<Navigate to='/login' />} />
            </Routes>
          ) : (
            <Routes>
              <Route path='/' element={<Homepage />} />
              <Route path='/welcome' element={<WelcomePage />} />
              {/* <Route path='/v1' element={<Homepage />} /> */}
              <Route path='/history' element={<HistoryPage />} />
              <Route path='/profile' element={<ProfilePage />} />
              <Route path='/pdf-chat' element={<PDFChat />} />
              <Route path='/my-subscription' element={<SubscriptionPage />} />
              <Route path='/logout' element={<LogoutPage />} />
              <Route path='*' element={<Navigate to='/' />} />
            </Routes>
          )}
        </>
      )}
    </div>
  );
}

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
